import {
  Component,
  Inject,
  signal,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReportService, ReportPayload } from '../services/report.service';
import { finalize } from 'rxjs/operators';
import { AlertComponent } from '../alert/alert.component';

export interface ReportModalData {
  reportableType: 'user' | 'project' | 'comment';
  reportableId: number;
  displayName: string;
}

@Component({
  selector: 'app-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    TranslateModule,
    AlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './report-modal.component.html',
})
export class ReportModalComponent {
  private reportService = inject(ReportService);
  private translate = inject(TranslateService);

  isLoading = signal(false);
  isSuccess = signal(false);
  errorMessage = signal<string | null>(null);

  selectedReason = signal('');
  description = signal('');

  reasons = [
    { value: 'spam', label: 'REPORT_MODAL.REASON_SPAM' },
    { value: 'harassment', label: 'REPORT_MODAL.REASON_HARASSMENT' },
    {
      value: 'inappropriate_content',
      label: 'REPORT_MODAL.REASON_INAPPROPRIATE',
    },
    { value: 'misinformation', label: 'REPORT_MODAL.REASON_MISINFORMATION' },
    { value: 'copyright', label: 'REPORT_MODAL.REASON_COPYRIGHT' },
    { value: 'hate_speech', label: 'REPORT_MODAL.REASON_HATE_SPEECH' },
    { value: 'other', label: 'REPORT_MODAL.REASON_OTHER' },
  ];

  constructor(
    public dialogRef: MatDialogRef<ReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportModalData,
  ) {}

  onClose(): void {
    this.dialogRef.close(this.isSuccess());
  }

  onSubmit(): void {
    if (!this.selectedReason()) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const payload: ReportPayload = {
      reportable_type: this.data.reportableType,
      reportable_id: this.data.reportableId,
      reason: this.selectedReason(),
      description: this.description().trim() || undefined,
    };

    this.reportService
      .sendReport(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {
          this.isSuccess.set(true);
          setTimeout(() => this.onClose(), 2000);
        },
        error: (err) => {
          if (err.status === 409) {
            this.errorMessage.set(
              this.translate.instant('REPORT_MODAL.ERROR_ALREADY_REPORTED'),
            );
          } else {
            this.errorMessage.set(
              this.translate.instant('REPORT_MODAL.ERROR_GENERIC'),
            );
          }
        },
      });
  }
}
